/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Pill, Plus, Send, Info } from 'lucide-react';
import PatientPicker from '@/components/voicediary/PatientPicker';
import InteractionAlerts from '@/components/fse/InteractionAlerts';
import LocalInteractionAlerts from '@/components/fse/LocalInteractionAlerts';
import DictationButton from '@/components/fse/DictationButton';
import RxTemplatePicker from '@/components/fse/RxTemplatePicker';
import SignPrescriptionDialog from '@/components/fse/SignPrescriptionDialog';
import ActiveTherapyPanel from '@/components/fse/ActiveTherapyPanel';
import { PrescriptionItemRow } from '@/components/fse/prescription/PrescriptionItemRow';
import { usePrescriptionChecks } from '@/components/fse/prescription/usePrescriptionChecks';
import {
  EXEMPTIONS, MAX_RX_ITEMS, emptyItem, itemFromDictation, itemFromTemplate,
} from '@/components/fse/prescription/prescriptionConstants';
import { createFsePrescription } from '@/lib/fseLombardia';

/**
 * PrescriptionCreator — Ricetta Rossa Elettronica (FSE Lombardia).
 *
 * Refactor note (Code Quality): la riga farmaco, i controlli di sicurezza
 * (doppioni + interazioni) e le costanti vivono in `components/fse/prescription/*`.
 */
export const PrescriptionCreator = ({ onCreated }) => {
  const [patient, setPatient] = useState({ id: null, label: '' });
  const [diagnosis, setDiagnosis] = useState('');
  const [exemption, setExemption] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [busy, setBusy] = useState(false);

  // OTP sign dialog state
  const [signDoc, setSignDoc] = useState(null); // { id, prescription }

  // Active-therapy of the current patient (used for duplicate detection)
  const [currentTherapyActives, setCurrentTherapyActives] = useState([]);

  const { duplicates, interactions } = usePrescriptionChecks(items, patient?.id, currentTherapyActives);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => (prev.length >= MAX_RX_ITEMS ? prev : [...prev, emptyItem()]));
  const removeItem = (i) => setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const handleDictatedItem = (parsed) => {
    // Fill the first empty item OR append a new one (max 6).
    setItems((prev) => {
      const emptyIdx = prev.findIndex((it) => !it.name && !it.posology);
      const newItem = itemFromDictation(parsed);
      if (emptyIdx >= 0) {
        return prev.map((it, idx) => (idx === emptyIdx ? newItem : it));
      }
      return prev.length >= MAX_RX_ITEMS ? prev : [...prev, newItem];
    });
    toast.success(`Dettatura interpretata: ${parsed.name || parsed.raw}`);
  };

  const handleLoadTemplate = (tpl) => {
    // Overwrite current items with the template drugs (preserve at least 1 slot)
    const loaded = (tpl.drugs || []).slice(0, MAX_RX_ITEMS).map(itemFromTemplate);
    setItems(loaded.length ? loaded : [emptyItem()]);
    if (tpl.diagnosis && !diagnosis) setDiagnosis(tpl.diagnosis);
    if (tpl.notes && !notes) setNotes(tpl.notes);
  };

  const canSubmit = patient.id && diagnosis.trim() && items.length > 0
    && items.every((it) => it.name.trim() && it.posology.trim());

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Compila paziente, diagnosi e almeno un farmaco con posologia');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        patient_id: patient.id,
        diagnosis: diagnosis.trim(),
        exemption_code: exemption || null,
        notes: notes.trim() || null,
        items: items.map((it) => ({
          name: it.name.trim(),
          aic: it.aic?.trim() || null,
          quantity: it.quantity?.trim() || '1',
          posology: it.posology.trim(),
          notes: it.notes?.trim() || null,
        })),
      };
      const doc = await createFsePrescription(payload);
      const nre = doc?.prescription?.nre;
      toast.success(nre ? `Ricetta emessa · NRE ${nre}. Ora firmala con OTP.` : 'Ricetta emessa. Ora firmala con OTP.');
      onCreated?.(doc);
      // Open the OTP dialog to prompt the doctor for signature immediately
      setSignDoc(doc);
      // reset only prescription fields (keep patient for follow-ups)
      setDiagnosis(''); setExemption(''); setNotes(''); setItems([emptyItem()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore emissione ricetta');
    } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px] items-start">
      <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Pill className="h-4 w-4 text-primary" /> Nuova Ricetta Rossa Elettronica
        </CardTitle>
        <CardDescription>
          Prescrivi farmaci coperti dal SSN. Richiede consenso FSE del paziente attivo.
          Il NRE (Numero Ricetta Elettronica) viene generato automaticamente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-testid="prescription-creator">
        <div>
          <Label className="text-xs">Paziente</Label>
          <PatientPicker
            endpoint="/voice-diary/patients"
            value={patient}
            onChange={setPatient}
            placeholder="Cerca paziente…"
            testIdPrefix="rx-patient-picker"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label className="text-xs">Diagnosi principale *</Label>
              <DictationButton
                mode="raw"
                size="sm"
                label="Detta"
                testId="rx-diagnosis-dictate"
                onRawText={(chunk) => {
                  setDiagnosis((prev) => (prev ? `${prev} ${chunk}` : chunk).trim().slice(0, 200));
                  toast.success('Diagnosi aggiornata');
                }}
              />
            </div>
            <Input
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value.slice(0, 200))}
              placeholder="es. Ipertensione arteriosa essenziale"
              data-testid="rx-diagnosis"
            />
          </div>
          <div>
            <Label className="text-xs">Codice esenzione</Label>
            <select
              className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={exemption}
              onChange={(e) => setExemption(e.target.value)}
              data-testid="rx-exemption"
            >
              {EXEMPTIONS.map((ex) => (
                <option key={ex.code} value={ex.code}>{ex.code ? `${ex.code} · ${ex.label}` : ex.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Items */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-xs">Farmaci prescritti (max {MAX_RX_ITEMS})</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <DictationButton size="sm" label="Detta ricetta" testId="rx-dictate-btn" onParsed={handleDictatedItem} />
              <Button size="sm" variant="outline" onClick={addItem} disabled={items.length >= MAX_RX_ITEMS} data-testid="rx-add-item">
                <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi farmaco
              </Button>
            </div>
          </div>

          {/* Client-side offline interaction alerts (local JSON dictionary). */}
          <LocalInteractionAlerts
            drugs={items.map((it) => it.name).filter(Boolean)}
            activeTherapy={currentTherapyActives || []}
          />

          {/* Prescription templates (preferiti) */}
          <RxTemplatePicker
            currentForm={{ specialty: null, diagnosis, drugs: items, notes }}
            onLoad={handleLoadTemplate}
          />

          {items.map((it, i) => (
            <PrescriptionItemRow
              key={it._key || `rx-existing-${i}`}
              item={it}
              index={i}
              canRemove={items.length > 1}
              duplicateActive={duplicates[i]}
              onChange={(patch) => setItem(i, patch)}
              onRemove={() => removeItem(i)}
            />
          ))}
        </div>

        <div>
          <Label className="text-xs">Note aggiuntive</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                    placeholder="Follow-up, avvertenze, contatti…" rows={2}
                    data-testid="rx-general-notes" />
        </div>

        <InteractionAlerts
          findings={interactions.findings}
          counts={interactions.counts}
          currentTherapy={interactions.currentTherapy}
          evaluated={interactions.evaluated}
          loading={interactions.loading}
        />

        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="h-3 w-3 mt-0.5" />
          <span>
            Documento generato in formato CDA R2 con LOINC 57833-6 (Prescription for medication) e sezione farmaci strutturata.
            NRE simulato con prefisso 030 (Regione Lombardia). Firma OTP obbligatoria prima della trasmissione a SISS (mTLS simulato).
          </span>
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={submit} disabled={busy || !canSubmit} data-testid="rx-submit">
            <Send className="h-4 w-4 mr-1" /> Emetti Ricetta
          </Button>
        </div>
      </CardContent>
      <SignPrescriptionDialog
        docId={signDoc?.id || null}
        open={!!signDoc}
        onOpenChange={(v) => { if (!v) setSignDoc(null); }}
        onSigned={() => { setSignDoc(null); onCreated?.(); }}
      />
      </Card>

      <div className="lg:sticky lg:top-4">
        <ActiveTherapyPanel patientId={patient.id} onLoaded={setCurrentTherapyActives} />
      </div>
    </div>
  );
};

export default PrescriptionCreator;
